import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import personsService from '../../services/personsService';
import personNotesService from '../../services/personNotesService';
import personAppointmentsService from '../../services/personAppointmentsService';

/**
 * Carrega uma Person por id (para a tela/modal de detalhe).
 */
export function usePerson(personId) {
  const query = useQuery({
    queryKey: ['person', personId],
    queryFn: () => personsService.getById(personId),
    enabled: !!personId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    person: query.data || null,
    loading: query.isLoading,
    error: query.error,
    reload: query.refetch,
  };
}

/**
 * Notas/fichas de uma Person + mutations (criar, editar, excluir, anexar, remover anexo).
 */
export function usePersonNotes(personId) {
  const queryClient = useQueryClient();
  const key = ['personNotes', personId];

  const query = useQuery({
    queryKey: key,
    queryFn: () => personNotesService.list(personId),
    enabled: !!personId,
    staleTime: 60 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: key });

  const createMutation = useMutation({
    mutationFn: (payload) => personNotesService.create(personId, payload),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({ noteId, payload }) => personNotesService.update(personId, noteId, payload),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (noteId) => personNotesService.remove(personId, noteId),
    onSuccess: invalidate,
  });

  const attachMutation = useMutation({
    mutationFn: ({ noteId, file, ownerUserId }) => personNotesService.uploadAndAttach(personId, noteId, file, ownerUserId),
    onSuccess: invalidate,
  });

  const removeAttachmentMutation = useMutation({
    mutationFn: ({ noteId, attachmentId }) => personNotesService.removeAttachment(personId, noteId, attachmentId),
    onSuccess: invalidate,
  });

  return {
    notes: query.data || [],
    loading: query.isLoading,
    error: query.error,
    reload: invalidate,

    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,

    updateAsync: (noteId, payload) => updateMutation.mutateAsync({ noteId, payload }),
    isUpdating: updateMutation.isPending,

    removeAsync: removeMutation.mutateAsync,
    isRemoving: removeMutation.isPending,

    attachAsync: (noteId, file, ownerUserId) => attachMutation.mutateAsync({ noteId, file, ownerUserId }),
    isAttaching: attachMutation.isPending,

    removeAttachmentAsync: (noteId, attachmentId) => removeAttachmentMutation.mutateAsync({ noteId, attachmentId }),
    isRemovingAttachment: removeAttachmentMutation.isPending,
  };
}

/**
 * Histórico de agendamentos de uma Person (timeline).
 */
export function usePersonAppointments(personId) {
  const query = useQuery({
    queryKey: ['personAppointments', personId],
    queryFn: () => personAppointmentsService.getByPerson(personId),
    enabled: !!personId,
    staleTime: 60 * 1000,
  });

  return {
    appointments: query.data || [],
    loading: query.isLoading,
    error: query.error,
    reload: query.refetch,
  };
}
